import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Check, X, Flag, Film, Loader2, AlertTriangle, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../lib/auth";

export default function FestivalModeration() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("flagged");

  const load = () => {
    api.get(`/admin/festival/moderation${filter ? `?status=${filter}` : ""}`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { load(); }, [filter]);

  const approve = async (id) => {
    setBusy(id);
    try { await api.post(`/admin/festival/submissions/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    const reason = prompt("Rejection reason (shown to filmmaker):", "Violates festival guidelines");
    if (reason === null) return;
    setBusy(id);
    try { await api.post(`/admin/festival/submissions/${id}/reject`, { reason }); toast.success("Rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const waiveFee = async (id) => {
    if (!window.confirm("Waive the submission fee for this film? It'll auto-approve if pending payment.")) return;
    setBusy(id);
    try { await api.post(`/admin/festival/submissions/${id}/waive-fee`); toast.success("Fee waived"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const acceptOfficial = async (id, hasPoster) => {
    if (!hasPoster) { toast.error("Submission has no poster to stamp"); return; }
    if (!window.confirm("Mark as Official Selection? A laurel-stamped poster will be generated and the submission will be approved.")) return;
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/festival/submissions/${id}/accept-official`);
      toast.success(<div>
        🏆 Official Selection {data.year}!
        {data.official_poster_url && (<>
          {" — "}
          <a href={data.official_poster_url} target="_blank" rel="noreferrer" className="text-amber-300 underline">View stamped poster</a>
        </>)}
      </div>, { duration: 6000 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const revokeOfficial = async (id) => {
    if (!window.confirm("Revoke Official Selection? The stamped poster URL will be removed.")) return;
    setBusy(id);
    try { await api.post(`/admin/festival/submissions/${id}/revoke-official`); toast.success("Official Selection revoked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const setJudgingStatus = async (id, val) => {
    if (!val) return;
    setBusy(id);
    try {
      await api.post(`/admin/festival/submissions/${id}/judging-status`, { judging_status: val });
      toast.success(`Judging: ${val.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const setExtendedStatus = async (id, val) => {
    if (!val) return;
    setBusy(id);
    try {
      await api.post(`/admin/festival/submissions/${id}/extended-status`, { status: val });
      toast.success(`Status: ${val.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const aiPrescreen = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/festival/submissions/${id}/ai-prescreen`);
      const p = data.ai_prescreen;
      toast.success(
        <div className="text-xs">
          <div className="font-bold mb-1">🤖 AI Pre-screen · score {p.score}/10 · {p.recommendation}</div>
          {p.issues?.length > 0 && (
            <ul className="list-disc pl-4 space-y-0.5">
              {p.issues.map((i, k) => <li key={k}>{i}</li>)}
            </ul>
          )}
        </div>,
        { duration: 10000 }
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI pre-screen failed"); }
    finally { setBusy(null); }
  };

  const aiJudging = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/festival/submissions/${id}/ai-judging-suggestion`);
      const j = data.ai_judging;
      toast.success(
        <div className="text-xs">
          <div className="font-bold mb-1">🤖 Suggested: {j.suggested_tier.replace(/_/g, " ")} · {Math.round(j.confidence * 100)}% confident</div>
          <div className="text-gray-300">{j.reasoning}</div>
        </div>,
        { duration: 12000 }
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "AI judging failed"); }
    finally { setBusy(null); }
  };

  const aiTranscribe = async (id) => {
    if (!window.confirm("Generate Whisper subtitles (VTT) for this film? (25 MB max)")) return;
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/festival/submissions/${id}/ai-transcribe`);
      toast.success(
        <div>
          ✓ Subtitles generated ({data.transcript_chars} chars){" "}
          <a href={data.subtitles_url} target="_blank" rel="noreferrer" className="text-amber-300 underline">Download VTT</a>
        </div>,
        { duration: 8000 }
      );
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Transcription failed"); }
    finally { setBusy(null); }
  };

  const JUDGING_OPTS = [
    { v: "undecided", l: "Undecided" },
    { v: "selected", l: "Selected" },
    { v: "not_selected", l: "Not Selected" },
    { v: "award_winner", l: "Award Winner" },
    { v: "finalist", l: "Finalist" },
    { v: "semi_finalist", l: "Semi-Finalist" },
    { v: "quarter_finalist", l: "Quarter-Finalist" },
    { v: "nominee", l: "Nominee" },
    { v: "honorable_mention", l: "Honorable Mention" },
  ];
  const EXTENDED_OPTS = [
    { v: "", l: "— Set status —" },
    { v: "in_consideration", l: "In Consideration" },
    { v: "incomplete", l: "Incomplete" },
    { v: "disqualified", l: "Disqualified" },
    { v: "withdrawn", l: "Withdrawn" },
  ];

  if (!user?.is_admin) return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <div className="pt-32 px-6 text-center text-gray-400 max-w-md mx-auto">
        <Shield className="w-10 h-10 text-red-500 mx-auto mb-3" />
        Admin access required.
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />
      <main className="pt-28 px-6 lg:px-12 max-w-[1200px] mx-auto pb-20">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-widest text-red-400">
              <Shield className="w-3.5 h-3.5" /> Festival Moderation
            </div>
            <h1 className="text-4xl font-black">Submission Queue</h1>
            <p className="text-gray-500 text-sm mt-1">Review flagged or pending films. Auto-flag threshold: 3 reports.</p>
          </div>
          <div className="flex gap-2">
            {[
              { v: "flagged", l: "Flagged" },
              { v: "pending", l: "Pending" },
              { v: "pending_payment", l: "Awaiting fee" },
              { v: "rejected", l: "Rejected" },
              { v: "approved", l: "Approved" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`filter-${o.v}`}
                onClick={() => setFilter(o.v)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                  filter === o.v ? "bg-white text-black border-white" : "bg-transparent border-white/15 text-gray-400 hover:text-white hover:border-white/40"
                }`}
              >{o.l}</button>
            ))}
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-20 text-gray-500 bg-[#0f0f0f] border border-dashed border-white/10 rounded-lg">
            <Check className="w-10 h-10 mx-auto mb-3 opacity-40 text-green-500" />
            <p>Queue is clean — no {filter} submissions.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((s) => (
              <div key={s.submission_id} data-testid={`mod-row-${s.submission_id}`} className="bg-[#141414] border border-white/5 rounded-lg p-5">
                <div className="flex items-start gap-4">
                  {s.poster_url ? (
                    <img src={s.poster_url} alt="" className="w-16 h-24 object-cover rounded flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-24 rounded bg-black/40 flex items-center justify-center flex-shrink-0"><Film className="w-6 h-6 text-gray-600" /></div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/festival/films/${s.submission_id}`} className="text-lg font-bold hover:text-[#E50914] truncate" target="_blank">
                        {s.title}
                      </Link>
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border ${
                        s.status === "flagged" ? "bg-red-500/15 text-red-400 border-red-500/40" :
                        s.status === "pending" ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/40" :
                        s.status === "approved" ? "bg-green-500/15 text-green-400 border-green-500/40" :
                        "bg-gray-500/15 text-gray-400 border-gray-500/30"
                      }`}>{s.status}</span>
                      {s.ai_prescreen?.score && (
                        <span
                          data-testid={`ai-badge-${s.submission_id}`}
                          className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border inline-flex items-center gap-1 ${
                            s.ai_prescreen.recommendation === "approve" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" :
                            s.ai_prescreen.recommendation === "reject" ? "bg-red-500/15 text-red-300 border-red-500/40" :
                            "bg-violet-500/15 text-violet-200 border-violet-500/40"
                          }`}
                          title={(s.ai_prescreen.issues || []).join(" · ")}
                        >
                          🤖 {s.ai_prescreen.score}/10 · {s.ai_prescreen.recommendation}
                        </span>
                      )}
                      {s.judging_status && s.judging_status !== "undecided" && (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-amber-500/15 text-amber-300 border-amber-500/40">
                          🏅 {s.judging_status.replace(/_/g, " ")}
                        </span>
                      )}
                      {s.extended_status && (
                        <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-sky-500/15 text-sky-300 border-sky-500/40">
                          {s.extended_status.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    {/* Jury + extended status dropdowns */}
                    <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px]">
                      <label className="inline-flex items-center gap-1.5">
                        <span className="text-gray-500 uppercase tracking-widest text-[9px]">Judging</span>
                        <select
                          data-testid={`judging-select-${s.submission_id}`}
                          value={s.judging_status || "undecided"}
                          onChange={(e) => setJudgingStatus(s.submission_id, e.target.value)}
                          disabled={busy === s.submission_id}
                          className="bg-black/40 border border-amber-500/30 rounded px-2 py-1 text-[11px] text-amber-200 focus:outline-none focus:border-amber-400"
                        >
                          {JUDGING_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </label>
                      <label className="inline-flex items-center gap-1.5">
                        <span className="text-gray-500 uppercase tracking-widest text-[9px]">Status</span>
                        <select
                          data-testid={`extended-select-${s.submission_id}`}
                          value={s.extended_status || ""}
                          onChange={(e) => setExtendedStatus(s.submission_id, e.target.value)}
                          disabled={busy === s.submission_id}
                          className="bg-black/40 border border-sky-500/30 rounded px-2 py-1 text-[11px] text-sky-200 focus:outline-none focus:border-sky-400"
                        >
                          {EXTENDED_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                        </select>
                      </label>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {s.region || "—"} · {s.runtime_min || 0} min · by{" "}
                      <span className="text-gray-300">{s.filmmaker?.name || "Unknown"}</span>{" "}
                      <span className="text-gray-600">({s.filmmaker?.email})</span>
                    </div>
                    {s.synopsis && <p className="text-sm text-gray-400 mt-2 line-clamp-2 max-w-2xl">{s.synopsis}</p>}
                    {(s.submission_fee_amount > 0) && (
                      <div className="mt-2 inline-flex items-center gap-2 text-[11px]">
                        <DollarSign className="w-3 h-3 text-amber-400" />
                        <span className="text-gray-400">Fee:</span>
                        <span className="text-amber-300 font-semibold">
                          ${s.submission_fee_amount?.toFixed(2)} {(s.submission_fee_currency || "usd").toUpperCase()}
                        </span>
                        <span className="text-gray-500">({s.submission_fee_tier})</span>
                        {s.submission_fee_paid ? (
                          <span className="bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold">Paid</span>
                        ) : s.submission_fee_waived ? (
                          <span className="bg-sky-500/15 text-sky-400 border border-sky-500/30 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold">Waived</span>
                        ) : (
                          <span className="bg-amber-500/15 text-amber-400 border border-amber-500/30 rounded px-1.5 py-0.5 text-[9px] uppercase tracking-widest font-bold">Unpaid</span>
                        )}
                      </div>
                    )}
                    {s.report_count > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                        <span className="text-red-400 font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" /> {s.report_count} report{s.report_count > 1 ? "s" : ""}
                        </span>
                        {Object.entries(s.reports_by_reason || {}).map(([k, v]) => (
                          <span key={k} className="bg-red-500/10 border border-red-500/30 rounded px-2 py-0.5 text-[10px] uppercase tracking-widest text-red-300">
                            {k}: {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    {s.status !== "approved" && (
                      <button data-testid={`approve-${s.submission_id}`} onClick={() => approve(s.submission_id)} disabled={busy === s.submission_id} className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-green-500/30 text-green-300 hover:bg-green-500/10 disabled:opacity-50">
                        {busy === s.submission_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
                      </button>
                    )}
                    {s.status !== "rejected" && (
                      <button data-testid={`reject-${s.submission_id}`} onClick={() => reject(s.submission_id)} disabled={busy === s.submission_id} className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-red-500/30 text-red-300 hover:bg-red-500/10 disabled:opacity-50">
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                    )}
                    {(s.submission_fee_amount > 0) && !s.submission_fee_paid && !s.submission_fee_waived && (
                      <button
                        data-testid={`waive-fee-${s.submission_id}`}
                        onClick={() => waiveFee(s.submission_id)}
                        disabled={busy === s.submission_id}
                        className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50"
                      >
                        {busy === s.submission_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />} Waive fee
                      </button>
                    )}
                    {!s.is_official_selection ? (
                      <button
                        data-testid={`accept-official-${s.submission_id}`}
                        onClick={() => acceptOfficial(s.submission_id, !!s.poster_url)}
                        disabled={busy === s.submission_id || !s.poster_url}
                        title={s.poster_url ? "Stamp laurel + mark as Official Selection" : "Needs a poster first"}
                        className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 disabled:opacity-50"
                      >
                        🏆 Official Selection
                      </button>
                    ) : (
                      <button
                        data-testid={`revoke-official-${s.submission_id}`}
                        onClick={() => revokeOfficial(s.submission_id)}
                        disabled={busy === s.submission_id}
                        className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-amber-500/60 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25"
                      >
                        🏆 Revoke selection
                      </button>
                    )}
                    {/* AI helpers — pre-screen + judging suggestion + transcribe */}
                    <button
                      data-testid={`ai-prescreen-${s.submission_id}`}
                      onClick={() => aiPrescreen(s.submission_id)}
                      disabled={busy === s.submission_id}
                      title="Claude reads metadata and scores completeness"
                      className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-violet-500/40 text-violet-200 hover:bg-violet-500/10 disabled:opacity-50"
                    >
                      🤖 AI pre-screen{s.ai_prescreen?.score ? ` · ${s.ai_prescreen.score}/10` : ""}
                    </button>
                    <button
                      data-testid={`ai-judging-${s.submission_id}`}
                      onClick={() => aiJudging(s.submission_id)}
                      disabled={busy === s.submission_id}
                      title="Claude suggests a jury tier"
                      className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-violet-500/40 text-violet-200 hover:bg-violet-500/10 disabled:opacity-50"
                    >
                      🤖 Suggest tier{s.ai_judging?.suggested_tier ? ` · ${s.ai_judging.suggested_tier.replace(/_/g, " ")}` : ""}
                    </button>
                    <button
                      data-testid={`ai-transcribe-${s.submission_id}`}
                      onClick={() => aiTranscribe(s.submission_id)}
                      disabled={busy === s.submission_id || !s.film_url}
                      title={s.film_url ? "Whisper SRT subtitles (25 MB max)" : "Needs a film file first"}
                      className="btn-glass px-3 py-1.5 rounded text-xs font-semibold inline-flex items-center gap-1.5 border border-violet-500/40 text-violet-200 hover:bg-violet-500/10 disabled:opacity-50"
                    >
                      🎙 {s.subtitles_url ? "Re-transcribe" : "Auto-subtitles"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
